class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.recommendationsAbortController = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupCheckoutFallback();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.shouldRefreshCartDrawerBeforeOpen()) this.refreshCartDrawerFromCartPage();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.refreshDeferredSectionsAfterOpen();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (!parsedState.sections || !parsedState.sections[section.id]) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.setupCheckoutFallback();
    this.renderPendingContents();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });

    this.fetchCartDrawerSection()
      .then((html) => {
        this.renderCartDrawerHTML(html);
      })
      .catch((error) => {
        const drawer = document.getElementById('CartDrawer');
        if (drawer) {
          drawer.classList.remove('is-refreshing');
          drawer.removeAttribute('aria-busy');
        }
        console.error(error);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  fetchCartDrawerSection() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    const separator = cartUrl.includes('?') ? '&' : '?';
    return fetch(`${cartUrl}${separator}section_id=cart-drawer&_dr=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'text/html' },
    }).then((response) => {
      if (!response.ok) throw new Error('Could not refresh cart drawer');
      return response.text();
    });
  }

  refreshCartDrawerFromCartPage() {
    this.renderPendingContents();

    return this.fetchCartDrawerSection()
      .then((html) => {
        this.renderCartDrawerHTML(html);
      })
      .catch((error) => {
        const drawer = document.getElementById('CartDrawer');
        if (drawer) {
          drawer.classList.remove('is-refreshing');
          drawer.removeAttribute('aria-busy');
        }
        console.error(error);
      });
  }

  getHeaderCartItemCount() {
    const countElement = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    if (!countElement) return 0;

    const count = parseInt(countElement.textContent, 10);
    return Number.isNaN(count) ? 0 : count;
  }

  getDrawerCartItemCount() {
    const countElement = this.querySelector('.dr-cart-drawer__count');
    if (!countElement) return 0;

    const count = parseInt(countElement.textContent, 10);
    return Number.isNaN(count) ? 0 : count;
  }

  shouldRefreshCartDrawerBeforeOpen() {
    const drawer = document.getElementById('CartDrawer');
    if (drawer && drawer.classList.contains('is-refreshing')) return false;

    const headerCount = this.getHeaderCartItemCount();
    if (headerCount === 0) return false;

    return this.classList.contains('is-empty') || this.getDrawerCartItemCount() !== headerCount;
  }

  renderPendingContents() {
    this.classList.remove('is-empty');

    const drawer = document.getElementById('CartDrawer');
    if (drawer) {
      drawer.classList.add('is-refreshing');
      drawer.setAttribute('aria-busy', 'true');
    }

    const drawerItems = this.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.remove('is-empty');

    const items = this.querySelector('#CartDrawer-CartItems');
    if (items) {
      items.innerHTML = '<div class="dr-cart-drawer__refreshing" role="status">Updating your cart...</div>';
    }
  }

  renderCartDrawerHTML(html) {
    const sourceDrawer = this.getSectionDOM(html, 'cart-drawer');
    const source = this.getSectionDOM(html, '#CartDrawer');
    const target = document.getElementById('CartDrawer');
    if (!source || !target) return;

    if (sourceDrawer) this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));

    target.innerHTML = source.innerHTML;
    target.classList.remove('is-refreshing');
    target.removeAttribute('aria-busy');
    this.setupCheckoutFallback();

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.refreshDeferredSections({ force: true });
  }

  getRecommendationsSlot() {
    return this.querySelector('[data-dr-cart-recommendations-slot]');
  }

  getRecommendationsUrl() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    const separator = cartUrl.includes('?') ? '&' : '?';
    return `${cartUrl}${separator}section_id=cart-drawer-recommendations&_dr=${Date.now()}`;
  }

  loadRecommendations(options = {}) {
    const force = options.force === true;
    const slot = this.getRecommendationsSlot();
    if (!slot || this.classList.contains('is-empty')) return Promise.resolve();
    if (!force && slot.dataset.drCartRecommendationsLoaded === 'true') return Promise.resolve();
    if (!force && slot.dataset.drCartRecommendationsLoading === 'true') return Promise.resolve();

    if (this.recommendationsAbortController) this.recommendationsAbortController.abort();
    this.recommendationsAbortController = new AbortController();

    slot.dataset.drCartRecommendationsLoading = 'true';
    slot.classList.add('dr-cart-recommendations--loading');

    return fetch(this.getRecommendationsUrl(), {
      cache: 'no-store',
      signal: this.recommendationsAbortController.signal,
      headers: { Accept: 'text/html' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Could not load cart recommendations');
        return response.text();
      })
      .then((html) => {
        const source = this.getSectionDOM(html, '.dr-cart-recommendations');

        if (!source) {
          slot.remove();
          return;
        }

        source.setAttribute('data-dr-cart-recommendations-slot', '');
        source.dataset.drCartRecommendationsLoaded = 'true';
        source.dataset.drCartRecommendationsLoading = 'false';
        source.classList.remove('dr-cart-recommendations--loading');
        slot.replaceWith(source);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        slot.dataset.drCartRecommendationsLoading = 'false';
        slot.classList.remove('dr-cart-recommendations--loading');
        console.error(error);
      });
  }

  refreshDeferredSections(options = {}) {
    return this.loadRecommendations(options);
  }

  refreshDeferredSectionsAfterOpen() {
    const drawer = document.getElementById('CartDrawer');
    if (drawer && drawer.classList.contains('is-refreshing')) return Promise.resolve();
    return this.refreshDeferredSections();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setupCheckoutFallback() {
    const checkoutLink = this.querySelector('.dr-cart-drawer__checkout-link');
    if (!checkoutLink || checkoutLink.dataset.drCheckoutFallbackReady === 'true') return;

    const goToCheckout = (event) => {
      if (checkoutLink.getAttribute('aria-disabled') === 'true') return;
      if (event) event.preventDefault();
      window.location.href = '/checkout';
    };

    checkoutLink.dataset.drCheckoutFallbackReady = 'true';
    checkoutLink.addEventListener('click', goToCheckout);
    checkoutLink.addEventListener('touchstart', goToCheckout, { passive: false });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
